<template>
  <transition name="modal">
    <div class="overlay-bg">
      <div class="overlay">
        <button class="overlay__close-btn" @click="$emit('close-modal')">
          <span class="overlay__close-btn-text">Close</span>
        </button>

        <div class="container" data-testid="signup-share-modal">
          <h1>Invite Your Housemates</h1>

          <form
            class="form form--email"
            novalidate
            autocomplete="off"
            @submit.prevent="formSubmit"
            v-if="!emailSent"
          >
            <div>
              <fieldset
                v-for="(houseMate, index) in houseMates"
                :key="houseMate.id"
                class="form__fieldset"
              >
                <h3>Housemate {{ index + 1 }}</h3>

                <div
                  class="form-item"
                  :class="{
                    '--has-error': $v.houseMates.$each[index].email.$error,
                  }"
                >
                  <label class="form__label" :for="'email' + index">
                    <span class="form__label-text">Email address</span>
                    <FormError
                      v-if="
                        $v.houseMates.$each[index].email.$error &&
                          !$v.houseMates.$each[index].email.email
                      "
                      data-testid="valid-email-error"
                      msg="Valid email address required"
                    />
                  </label>
                  <input
                    type="text"
                    :name="'email' + index"
                    :id="'email' + index"
                    :data-testid="'email' + index"
                    class="form__input"
                    v-model="houseMates[index].email"
                  />
                </div>

                <div
                  class="form-item"
                  :class="{
                    '--has-error': $v.houseMates.$each[index].fullname.$error,
                  }"
                >
                  <label class="form__label" :for="'fullname' + index">
                    <span class="form__label-text">Full name</span>
                    <FormError
                      v-if="
                        $v.houseMates.$each[index].fullname.$error &&
                          !$v.houseMates.$each[index].fullname.required
                      "
                      data-testid="housemate-name-error"
                      msg="Full name of housemate required"
                    />
                  </label>
                  <input
                    type="text"
                    :name="'fullname' + index"
                    :id="'fullname' + index"
                    :data-testid="'fullname' + index"
                    class="form__input"
                    v-model="houseMates[index].fullname"
                  />
                </div>

                <div class="form-item">
                  <label class="form__label" :for="'tel' + index">
                    <span class="form__label-text"
                      >Phone number (optional)</span
                    >
                  </label>
                  <input
                    type="tel"
                    :name="'tel' + index"
                    :id="'tel' + index"
                    :data-testid="'tel' + index"
                    class="form__input"
                    v-model="houseMates[index].tel"
                  />
                </div>
              </fieldset>
            </div>

            <FormError
              class="general-error"
              v-if="emailSendError"
              data-testid="email-send-error"
              msg="There has been a problem sending the email, try again or note down share link for reference"
            />

            <FormError
              class="general-error"
              v-if="!detailsFilled"
              data-testid="housemate-details-error"
              msg="You need to fill in at least one housemate's details correctly"
            />

            <button
              class="btn btn--big btn--full-width form__submit"
              type="submit"
              data-testid="send-invite-button"
            >
              Send invite
            </button>
          </form>

          <div class="form-success" v-if="emailSent">
            <EnvelopeIcon width="50px" height="38px" aria-role="presentation" />
            <h4>Invites Sent!</h4>
            <p>
              Give your housemates a nudge to check their emails and sign up.
              Then put your feet up knowing your bills are sorted - easy!
            </p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  email,
  minLength,
  required,
  requiredIf,
} from "vuelidate/lib/validators";
import axiosInstance from "@/utility/axiosInstance";
import FormError from "@/components/forms/FormError.vue";
import EnvelopeIcon from "@/assets/svg/icon-envelope.svg";

export default {
  name: "SignupShareOverlay",
  props: {
    guid: {
      type: String,
      required: true,
    },
    remainingHousemateCount: {
      type: Number,
      required: true,
      default: 4,
    },
  },
  data() {
    return {
      detailsFilled: true,
      emailSent: false,
      emailSendError: false,
      houseMates: [],
    };
  },
  computed: {
    validHousemates() {
      return Object.keys(this.$v.houseMates.$each.$iter).reduce((acc, key) => {
        let validator = this.$v.houseMates.$each.$iter[key];
        if (
          !validator.$invalid &&
          (validator.email.$model !== "" && validator.fullname.$model !== "")
        ) {
          acc.push(this.$v.houseMates.$each.$iter[key].$model);
        }
        return acc;
      }, []);
    },
    validHousematesWithoutId() {
      // eslint-disable-next-line no-unused-vars
      return this.validHousemates.map(({ ...keepAttrs }) => keepAttrs);
    },
  },
  methods: {
    formSubmit() {
      this.$emit("inviteBtnSubmit");

      this.$v.$touch();

      if (this.validHousemates.length) {
        this.detailsFilled = true;
      } else {
        this.detailsFilled = false;
        return false;
      }

      if (!this.$v.$invalid) {
        this.$store.commit("SHOW_LOADING_PANEL");

        axiosInstance
          .post("/invite_housemates", {
            guid: this.guid,
            housemates: this.validHousematesWithoutId,
          })
          .then(() => {
            this.emailSent = true;
          })
          .catch(() => {
            this.emailSendError = true;
            this.emailSent = false;
          })
          .then(() => {
            this.$store.commit("HIDE_LOADING_PANEL");
          });
      } else {
        return false;
      }
    },
  },
  created() {
    // build the housemates data dynamically
    for (let i = 0; i < this.remainingHousemateCount; i++) {
      this.houseMates.push({
        id: "houseMate" + i,
        email: "",
        fullname: "",
        tel: "",
      });
    }
  },
  validations: {
    houseMates: {
      required,
      minLength: minLength(1),
      $each: {
        email: {
          email,
        },
        fullname: {
          required: requiredIf(function(vm) {
            return vm.email;
          }),
        },
      },
    },
  },
  components: {
    FormError,
    EnvelopeIcon,
  },
};
</script>

<style lang="scss" scoped>
.container {
  padding-left: $spacing-medium;
  padding-right: $spacing-medium;
  text-align: left;
}

h1 small {
  display: block;
  font-size: 2rem;
  font-weight: $font-regular;
}

h3 {
  font-size: 2rem;
  font-weight: $font-regular;
  color: $black;
  margin: 0 0 $spacing-medium;
}

.form--email {
  margin-bottom: $spacing-large;
}

.form-success {
  text-align: center;
  color: $white;
  background-color: $brand-green;
  padding: $spacing-medium;
  border-radius: $border-radius-small;

  svg {
    width: 50px;
    height: 38px;
    margin: $spacing-small auto $spacing-medium;
  }

  h4 {
    font-weight: $font-regular;
    margin: 0 0 $spacing-small;
  }

  p {
    font-size: 1.6rem;
    margin: 0;
  }
}

.general-error {
  margin-bottom: $spacing-medium;
}
</style>
